import type { FC } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import type { IconName, SWReactIconProps } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { CircleSwitcherItem, Switcher } from "@similarweb/ui-components";

import { getDevicesWithAvailabilityForSelectedCampaign } from "app/components/campaigns/queryBar-report/reports-pages-filters/locationPicker/CampaignsLocationFilterUtils";
import { SmallTitle } from "app/components/dashboard/widget-wizard/components/campaigns/CampaignsDashboardWizardStyled";
import type { IStatefullIconSwitcherItem } from "app/components/dashboard/widget-wizard/components/StatefullIconSwitcher";
import { TrafficSource } from "app/components/dashboard/widget-wizard/utils/dashboardWizardTrafficSourceGenerator";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import I18n from "pro-features/components/WithTranslation/src/I18n";

const { blue, carbon } = colorsPalettes;

interface IAvailableTrafficSources extends IStatefullIconSwitcherItem {
    icon: IconName;
    selected: boolean;
}

interface ICampaignsDashboardWizardDeviceProps {
    selectedTrafficSource: string;
    changeDevice: (id) => void;
    selectedCampaign: IRTCurrentCampaignData | null;
}

const SwitcherWrapper = styled.div`
    width: 96px;
`;

const SwitcherContainer = styled(Switcher)`
    border-right: 1px solid ${carbon[50]};
    height: fit-content;
    display: flex;
`;

const DEVICES_LIST = [
    new TrafficSource(TrafficSource.DESKTOP, TrafficSource.DESKTOP_KEY, TrafficSource.DESKTOP_ICON),
    new TrafficSource(TrafficSource.MOBILE, TrafficSource.MOBILE_KEY, TrafficSource.MOBILE_ICON),
] as IAvailableTrafficSources[];

const CampaignsDashboardWizardDevice: FC<ICampaignsDashboardWizardDeviceProps> = ({
    selectedTrafficSource,
    changeDevice,
    selectedCampaign,
}) => {
    const availableDevices = getDevicesWithAvailabilityForSelectedCampaign(
        DEVICES_LIST,
        selectedCampaign?.scrapingConfigurations || [],
    );
    const initiateTrafficSource = (): IAvailableTrafficSources[] => {
        if (selectedCampaign) {
            return availableDevices.map((item) => {
                const isAllDevicesSameAsItem = selectedCampaign?.scrapingConfigurations?.every(
                    (sc) => sc.device === item.id,
                );
                const selected = item.id.includes(selectedTrafficSource) || isAllDevicesSameAsItem;
                const disabled = selectedCampaign
                    ? selectedCampaign?.scrapingConfigurations?.every((sc) => !item.id.includes(sc.device))
                    : true;
                return {
                    ...item,
                    selected,
                    disabled,
                };
            });
        }
        return availableDevices;
    };
    const trafficSources = initiateTrafficSource();
    const onSelect = (selectedIndex: number) => {
        changeDevice(availableDevices[selectedIndex].id);
    };

    const switcherItems = trafficSources.map((item, index) => {
        const iconProps = {
            iconName: item.icon,
            size: "xs",
            ...(item.selected ? { iconColor: blue[400] } : {}),
        } as SWReactIconProps;

        return (
            <CircleSwitcherItem
                key={`${item.id}${index}`}
                disabled={item.disabled}
                selected={item.selected}
            >
                <SWReactIcons {...iconProps} />
            </CircleSwitcherItem>
        );
    });

    const selectedIndex = trafficSources.findIndex((item) => item.selected);
    return (
        <SwitcherWrapper>
            <SmallTitle>
                <I18n>home.dashboards.wizard.trafficSource.title</I18n>
            </SmallTitle>
            <SwitcherContainer
                selectedIndex={selectedIndex}
                onItemClick={onSelect}
            >
                {switcherItems}
            </SwitcherContainer>
        </SwitcherWrapper>
    );
};

export default CampaignsDashboardWizardDevice;
